/**
 * `mrr_lost` é gravado NEGATIVO no banco (é uma perda). A UI sempre mostra a
 * magnitude — "US$ 384,00 perdidos", não "US$ -384,00" — e o sinal vira cor/ícone.
 * Todo componente formata daqui; nenhum faz Math.abs solto.
 */
export const magnitude = (v: unknown): number => Math.abs(Number(v ?? 0));

/**
 * Os valores são USD. A cadeia Nekt → Supabase não carrega coluna de moeda
 * (`lost_revenue_raw.mrr_lost` é numeric puro), então a moeda é decisão de
 * apresentação e vive só aqui — nenhum componente formata dinheiro por conta.
 *
 * Formato: `US$ 1.234,56` — símbolo explícito (não só "$", pra nunca ser lido
 * como real) com separadores pt-BR, coerentes com o resto da UI, que já usa
 * vírgula decimal em "0,7 d" e "39,4%".
 */
const money = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const USD = (v: unknown): string => `US$ ${money.format(magnitude(v))}`;

/** Eixos de gráfico: "6,4k" / "216k" em vez do valor cheio (padrão do mockup). */
export function compact(v: unknown): string {
  const n = magnitude(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  if (n >= 1_000) return `${(n / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

export const int = (v: unknown): number => Math.trunc(Number(v ?? 0));

export const pct = (v: unknown, digits = 1): string =>
  `${Number(v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;

/**
 * Datas do Postgres chegam como "2026-08-29T03:00:00.000Z" (DATE + offset -03).
 * Formatar com `new Date(...)` local devolveria o dia anterior; por isso os
 * campos DATE são cortados na string antes de virar Date.
 */
function dateParts(v: unknown): [number, number, number] | null {
  if (!v) return null;
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** "29/08" — usado no Event Card, onde o ano é ruído. */
export function shortDate(v: unknown): string {
  const p = dateParts(v);
  if (!p) return '—';
  return `${String(p[2]).padStart(2, '0')}/${String(p[1]).padStart(2, '0')}`;
}

/** "29/08/2026" */
export function fullDate(v: unknown): string {
  const p = dateParts(v);
  if (!p) return '—';
  return `${String(p[2]).padStart(2, '0')}/${String(p[1]).padStart(2, '0')}/${p[0]}`;
}

/** "27/08 · 09:14" — timestamps de auditoria, aí sim com hora local. */
export function stamp(v: unknown): string {
  if (!v) return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} · ${hh}:${mi}`;
}

/** "YYYY-MM-DD" no fuso local — formato que os filtros do backend esperam. */
export function isoDay(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

/**
 * `email` e `telefone` vêm do próprio evento como string separada por vírgula
 * ("a@x.com, b@y.com"). Isso é o que alimenta o Contact Semaphore sem join ao
 * vivo por linha — exigência explícita do PRD §9-A.
 */
export function splitContacts(raw: unknown): string[] {
  if (raw == null) return [];
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
